import type { TtqaPathRun } from '@kelilearning/psycheflow-contracts';
import type { TtqSession, TtqSessionParticleEntry } from '../../services/ttqService';
import { resolveTtqParticleTitle, coerceReadablePlainText } from '../../utils/ttqDisplay';
import { resolvePapOverviewLabel } from './papDisplay';

export type TtqBirdsEyeCellRole = 'original' | 'derived' | 'converge';

export type TtqBirdsEyeCell = {
  particleId: string;
  role: TtqBirdsEyeCellRole;
  entry: TtqSessionParticleEntry;
  /** PAP 概要优先；无 PAP 时回退题目标题 */
  thumbLabel: string;
};

export type TtqBirdsEyeColumn = {
  /** 列顶原始颗粒 id；孤儿衍生列为 `__orphan__` */
  sourceId: string;
  cells: TtqBirdsEyeCell[];
};

export type TtqBirdsEyePlacedCell = TtqBirdsEyeCell & {
  x: number;
  y: number;
  /** 收敛完整卡用；缩略格默认取 layout cellW/H */
  width?: number;
  height?: number;
};

/** 多对一收敛完整卡（与一对多卡片同宽量级） */
export const TTQ_BIRDS_EYE_CONVERGE_CARD_W = 520;
export const TTQ_BIRDS_EYE_CONVERGE_CARD_H = 440;
/** 收敛卡之间横向间距，避免互相遮挡 */
export const TTQ_BIRDS_EYE_CONVERGE_CARD_GAP = 56;
/** 缩略格阵列底边到收敛带的间距 */
export const TTQ_BIRDS_EYE_CONVERGE_BAND_GAP = 168;

export type TtqBirdsEyeRelationEdge = {
  id: string;
  fromId: string;
  toId: string;
  /** 仅多对一收敛边会进入鸟瞰 */
  kind: 'converge';
};

export function resolveTtqBirdsEyeThumbLabel(entry: TtqSessionParticleEntry): string {
  const papLabel = resolvePapOverviewLabel(entry.pap);
  if (papLabel) {
    return coerceReadablePlainText(papLabel, 48) || papLabel.trim();
  }
  return resolveTtqParticleTitle(entry, 0);
}

function toCell(
  entry: TtqSessionParticleEntry,
  role: TtqBirdsEyeCellRole,
): TtqBirdsEyeCell {
  return {
    particleId: entry.particleId,
    role,
    entry,
    thumbLabel: resolveTtqBirdsEyeThumbLabel(entry),
  };
}

function listDoneRuns(pathRuns: TtqaPathRun[] | undefined): TtqaPathRun[] {
  return (pathRuns ?? [])
    .filter((run) => run.status === 'done')
    .slice()
    .sort((a, b) => String(a.completedAt || a.startedAt || '').localeCompare(String(b.completedAt || b.startedAt || '')));
}

function isManyToOneProductRun(run: TtqaPathRun): boolean {
  return run.pathId === 'cttq_converge'
    || run.ttqAngleId === 'cttq_converge'
    || run.pathId === 'pattern_unify'
    || run.ttqAngleId === 'pattern_unify';
}

/** 沿派生链上溯到会话内的最顶原始列根，避免「衍生又作源」时重复开列 */
function resolveColumnRootId(
  particleId: string,
  derivedToSource: Map<string, string>,
  presentIds: Set<string>,
): string {
  let current = particleId;
  const seen = new Set<string>();
  while (derivedToSource.has(current)) {
    if (seen.has(current)) break;
    seen.add(current);
    const parent = derivedToSource.get(current);
    if (!parent || parent === current) break;
    current = parent;
  }
  return presentIds.has(current) ? current : '__orphan__';
}

/** 衍生 → 最早完成引用源；列挂在最顶原始根下（多对一产出不另起列，进收敛带） */
export function buildTtqBirdsEyeColumns(session: TtqSession): TtqBirdsEyeColumn[] {
  const particles = session.particles ?? [];
  const byId = new Map(particles.map((entry) => [entry.particleId, entry]));
  const presentIds = new Set(byId.keys());
  const derivedToSource = new Map<string, string>();
  const convergeIds = new Set<string>();

  for (const run of listDoneRuns(session.pathRuns)) {
    const sourceId = String(run.sourceParticleId || '').trim()
      || (run.sourceParticleIds ?? []).map((id) => String(id || '').trim()).find(Boolean)
      || '';
    if (!sourceId) continue;
    for (const rawId of run.derivedParticleIds ?? []) {
      const derivedId = String(rawId || '').trim();
      if (!derivedId || derivedId === sourceId) continue;
      if (!derivedToSource.has(derivedId)) {
        derivedToSource.set(derivedId, sourceId);
      }
      if (isManyToOneProductRun(run)) {
        convergeIds.add(derivedId);
      }
    }
  }

  const columnMap = new Map<string, TtqBirdsEyeCell[]>();
  const ensure = (sourceId: string) => {
    const existing = columnMap.get(sourceId);
    if (existing) return existing;
    const next: TtqBirdsEyeCell[] = [];
    columnMap.set(sourceId, next);
    return next;
  };

  for (const entry of particles) {
    const derivedSource = derivedToSource.get(entry.particleId);
    if (derivedSource) {
      const columnRoot = resolveColumnRootId(derivedSource, derivedToSource, presentIds);
      const bucket = ensure(columnRoot);
      if (columnRoot !== '__orphan__' && !bucket.some((cell) => cell.particleId === columnRoot)) {
        const root = byId.get(columnRoot);
        if (root) {
          bucket.unshift(toCell(root, 'original'));
        }
      }
      if (!bucket.some((cell) => cell.particleId === entry.particleId)) {
        const role: TtqBirdsEyeCellRole = convergeIds.has(entry.particleId) ? 'converge' : 'derived';
        bucket.push(toCell(entry, role));
      }
      continue;
    }

    const bucket = ensure(entry.particleId);
    if (!bucket.some((cell) => cell.particleId === entry.particleId)) {
      bucket.unshift(toCell(entry, 'original'));
    }
  }

  const orderedSourceIds = particles
    .map((entry) => entry.particleId)
    .filter((id) => columnMap.has(id));
  if (columnMap.has('__orphan__')) orderedSourceIds.push('__orphan__');

  return orderedSourceIds
    .map((sourceId) => ({
      sourceId,
      cells: columnMap.get(sourceId) ?? [],
    }))
    .filter((column) => column.cells.length > 0);
}

/**
 * 缩略格按列排布；所有收敛完整卡统一放到阵列下方的横向带，互不重叠。
 * 收敛卡可拖动预览，松手后仍回到此横向排布。
 */
export function placeTtqBirdsEyeCells(
  columns: TtqBirdsEyeColumn[],
  options?: {
    hex?: number;
    cellW?: number;
    cellH?: number;
    colGap?: number;
    rowGap?: number;
    pad?: number;
    convergeCardW?: number;
    convergeCardH?: number;
    convergeCardGap?: number;
    convergeBandGap?: number;
  },
): { cells: TtqBirdsEyePlacedCell[]; width: number; height: number } {
  const cellW = options?.cellW ?? options?.hex ?? 112;
  const cellH = options?.cellH ?? options?.hex ?? 72;
  const colGap = options?.colGap ?? 28;
  const rowGap = options?.rowGap ?? 20;
  const pad = options?.pad ?? 64;
  const convergeCardW = options?.convergeCardW ?? TTQ_BIRDS_EYE_CONVERGE_CARD_W;
  const convergeCardH = options?.convergeCardH ?? TTQ_BIRDS_EYE_CONVERGE_CARD_H;
  const convergeCardGap = options?.convergeCardGap ?? TTQ_BIRDS_EYE_CONVERGE_CARD_GAP;
  const convergeBandGap = options?.convergeBandGap ?? TTQ_BIRDS_EYE_CONVERGE_BAND_GAP;
  const cells: TtqBirdsEyePlacedCell[] = [];
  const convergeQueue: TtqBirdsEyeCell[] = [];
  let thumbsBottom = pad;
  let maxRight = pad;
  let cursorX = pad;

  columns.forEach((column) => {
    const thumbs = column.cells.filter((cell) => cell.role !== 'converge');
    for (const cell of column.cells) {
      if (cell.role === 'converge') convergeQueue.push(cell);
    }
    const orphanOffset = column.sourceId === '__orphan__' ? 18 : 0;

    thumbs.forEach((cell, rowIndex) => {
      const x = cursorX;
      const y = pad + orphanOffset + rowIndex * (cellH + rowGap);
      cells.push({
        ...cell,
        x,
        y,
        width: cellW,
        height: cellH,
      });
      maxRight = Math.max(maxRight, x + cellW);
      thumbsBottom = Math.max(thumbsBottom, y + cellH);
    });

    cursorX += cellW + colGap;
  });

  const convergeY = thumbsBottom + (convergeQueue.length > 0 ? convergeBandGap : 0);
  convergeQueue.forEach((cell, index) => {
    const x = pad + index * (convergeCardW + convergeCardGap);
    cells.push({
      ...cell,
      x,
      y: convergeY,
      width: convergeCardW,
      height: convergeCardH,
    });
    maxRight = Math.max(maxRight, x + convergeCardW);
  });

  const maxBottom = convergeQueue.length > 0
    ? convergeY + convergeCardH
    : thumbsBottom;

  return {
    cells,
    width: maxRight + pad,
    height: maxBottom + pad,
  };
}

/** 查某多对一产物（CTTQ / 一解多题）的成员颗粒 id */
export function listTtqBirdsEyeConvergeMemberIds(
  session: TtqSession,
  convergeParticleId: string,
): string[] {
  const target = String(convergeParticleId || '').trim();
  if (!target) return [];
  for (const run of listDoneRuns(session.pathRuns)) {
    if (!isManyToOneProductRun(run)) continue;
    const derivedIds = (run.derivedParticleIds ?? []).map((id) => String(id || '').trim());
    if (!derivedIds.includes(target)) continue;
    const preferred = String(run.sourceParticleId || '').trim();
    const members = [
      ...((run.sourceParticleIds ?? []).map((id) => String(id || '').trim()).filter(Boolean)),
      ...(preferred ? [preferred] : []),
    ];
    return [...new Set(members)].filter((id) => id && id !== target);
  }
  return [];
}

/** 多对一边（cttq_converge / pattern_unify）：成员 → 聚合/综合颗粒 */
export function listTtqBirdsEyeRelationEdges(session: TtqSession): TtqBirdsEyeRelationEdge[] {
  const present = new Set(
    (session.particles ?? []).map((entry) => String(entry.particleId || '').trim()).filter(Boolean),
  );
  const edges: TtqBirdsEyeRelationEdge[] = [];
  const seen = new Set<string>();

  for (const run of listDoneRuns(session.pathRuns)) {
    if (!isManyToOneProductRun(run)) continue;

    const preferred = String(run.sourceParticleId || '').trim();
    const members = [
      ...((run.sourceParticleIds ?? []).map((id) => String(id || '').trim()).filter(Boolean)),
      ...(preferred ? [preferred] : []),
    ];
    const uniqueMembers = [...new Set(members)];
    const derivedIds = (run.derivedParticleIds ?? [])
      .map((id) => String(id || '').trim())
      .filter(Boolean);
    if (!derivedIds.length || !uniqueMembers.length) continue;

    for (const toId of derivedIds) {
      if (!present.has(toId)) continue;
      for (const fromId of uniqueMembers) {
        if (!present.has(fromId) || fromId === toId) continue;
        const key = `${fromId}->${toId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        edges.push({
          id: `rel:${run.id}:${fromId}:${toId}`,
          fromId,
          toId,
          kind: 'converge',
        });
      }
    }
  }

  return edges;
}
